import chunk from 'lodash/chunk'
import { SOUNDS, helpers } from 'react-wot-bootstrap'

import { formatMessage } from 'locale.js'
import { MONEY_TYPES } from 'constants'

import {
    getErrorMessage,
    getErrorHeader,
    getErrorReason,
    getMembersNames,
    formatPlayer,
    processReponseReasonArray,
    wrapInP,
    BASE_ERRORS,
    BASE_ERROR_HEADERS,
} from './base'
import { MESSAGE_PLAYERS_CHUNK } from './constants'
import messages from './translations'

export const RESOURCE_ALLOCATION_SUCCESS = 'NOTIFICATIONS_RESOURCE_ALLOCATION_SUCCESS'
export const RESOURCE_ALLOCATION_ERROR = 'NOTIFICATIONS_RESOURCE_ALLOCATION_ERROR'

export const resourceAllocationSuccessNotification = (amount, currency, playerNames) => {
    return dispatch => chunk(playerNames, MESSAGE_PLAYERS_CHUNK()).map((playerNamesChunk, ) => {
        const totalAllocated = amount * playerNamesChunk.length
        return dispatch(resourceAllocationSuccessSingleNotification(
            totalAllocated, currency, playerNamesChunk
        ))
    })
}

const getAllocationSuccessMessage = currency => {
    switch (currency) {
    case MONEY_TYPES.gold:
        return messages.resource_allocation_gold_success_text
    case MONEY_TYPES.credits:
        return messages.resource_allocation_credits_success_text
    case MONEY_TYPES.crystal:
        return messages.resource_allocation_crystal_success_text
    }
}

export const resourceAllocationSuccessSingleNotification = (amount, currency, playerNames) => {

    // todo: remove this check when w2c will accept headers for crystals and credits.
    const tooltipType = currency === MONEY_TYPES.gold ?
        `FinancialTransactionWith${currency.replace(/^\w/, c => c.toUpperCase())}` :
        'Warning'

    return {
        type: RESOURCE_ALLOCATION_SUCCESS,
        payload: {},
        meta: {
            webToClient: [{
                handler: 'sendNotification',
                params: {
                    type: tooltipType,
                    header: formatMessage(messages.resource_allocation_success_header),
                    message: formatMessage(getAllocationSuccessMessage(currency), {
                        amount: helpers.thousands(amount),
                        names: playerNames.map(name => formatPlayer(name)).join(', '),
                    }),
                },
            }, {
                handler: 'playSound',
                params: { soundId: SOUNDS.COINS },
            }],
        },
    }
}

const getAllocationErrorMessage = info => {
    switch (info.currency) {
    case MONEY_TYPES.gold:
        return {
            message: messages.resource_allocation_error_max_daily_gold_flow,
            days: info.flow_period_gold,
            amount: info.max_period_flow_gold,
        }
    case MONEY_TYPES.credits:
        return {
            message: messages.resource_allocation_error_max_daily_credits_flow,
            days: info.flow_period_credits,
            amount: info.max_period_flow_credits,
        }
    case MONEY_TYPES.crystal:
        return {
            message: messages.resource_allocation_error_max_daily_crystal_flow,
            days: info.flow_period_crystal,
            amount: info.max_period_flow_crystal,
        }
    }
}

const maxDailyResourceFlowMessage = (names, info) => {
    const { message, days, amount } = getAllocationErrorMessage(info)
    return formatMessage(message, {
        amount,
        days,
        names: names.map(memberName => formatPlayer(memberName)).join(', '),
    })
}

const getMinBattlesCount = info => {
    switch (info.currency) {
    case MONEY_TYPES.gold:
        return info.min_battles_count_gold
    case MONEY_TYPES.credits:
        return info.min_battles_count_credits
    case MONEY_TYPES.crystal:
        return info.min_battles_count_crystal
    }
}

const minBattlesCountMessage = (names, info) => (
    formatMessage(messages.resource_allocation_error_not_enough_battles, {
        battles: getMinBattlesCount(info),
        names: names.map((memberName) => formatPlayer(memberName)).join(', '),
    })
)

const notInClanMessage = (names) => (
    formatMessage(messages.resource_allocation_error_not_in_clan, {
        names: names.map((memberName) => formatPlayer(memberName)).join(', '),
    })
)

const bannedAccountsMessage = (names) => (
    formatMessage(messages.resource_allocation_error_permanently_banned, {
        names: names.map((memberName) => formatPlayer(memberName)).join(', '),
    })
)

export const getMessagesCannotAllocate = info => {
    const messagesSequence = []
    let temporalChunk = []
    let temporalChunkNamesLength = 0
    const limitsExceededIds = [
        info.reason_account_ids_gold,
        info.reason_account_ids_credits,
        info.reason_account_ids_crystal,
    ].find(ids => ids.length)
    if (limitsExceededIds && limitsExceededIds.length) {
        const reasonGoldNames = getMembersNames(limitsExceededIds, info.members)
        const processed = processReponseReasonArray(
            temporalChunk, temporalChunkNamesLength, reasonGoldNames,
            messagesSequence, info, maxDailyResourceFlowMessage
        )
        temporalChunkNamesLength = processed.tempLength
        temporalChunk = processed.temp
    }
    if (info.reason_battles_account_ids.length) {
        let reasonBattlesNames = getMembersNames(info.reason_battles_account_ids, info.members)
        const processed = processReponseReasonArray(
            temporalChunk, temporalChunkNamesLength, reasonBattlesNames,
            messagesSequence, info, minBattlesCountMessage
        )
        temporalChunkNamesLength = processed.tempLength
        temporalChunk = processed.temp
    }
    if (info.reason_not_in_clan_ids.length) {
        let reasonNotInClanNames = getMembersNames(info.reason_not_in_clan_ids, info.members)

        const processed = processReponseReasonArray(
            temporalChunk, temporalChunkNamesLength, reasonNotInClanNames,
            messagesSequence, info, notInClanMessage
        )
        temporalChunkNamesLength = processed.tempLength
        temporalChunk = processed.temp
    }
    if (info.reason_banned_account_ids.length) {
        let reasonBannedNames = getMembersNames(info.reason_banned_account_ids, info.members)

        const processed = processReponseReasonArray(
            temporalChunk, temporalChunkNamesLength, reasonBannedNames, messagesSequence, info, bannedAccountsMessage
        )
        temporalChunkNamesLength = processed.tempLength
        temporalChunk = processed.temp
    }

    if (temporalChunkNamesLength) {
        messagesSequence.push(temporalChunk)
    }

    return messagesSequence.map((message) => Array.isArray(message) ? wrapInP(...message) : message)
}

const RESOURCE_ALLOCATION_ERRORS = {
    ...BASE_ERRORS,
    no_gold: () => formatMessage(messages.resource_allocation_error_not_enough_resource),
    cannot_allocate_resource: getMessagesCannotAllocate,
    'default': () => formatMessage(messages.resource_allocation_server_error),
}

const RESOURCE_ALLOCATION_ERROR_HEADERS = {
    ...BASE_ERROR_HEADERS,
    cannot_allocate_resource: () => formatMessage(messages.resource_allocation_cannot_allocate),
}

export const resourceAllocationErrorNotification = (error = {}, accountId, members, currency) => {
    return dispatch => {
        const reason = getErrorReason(error)
        const additionalData = {
            reason_account_ids_gold: [],
            reason_account_ids_credits: [],
            reason_account_ids_crystal: [],
            reason_battles_account_ids: [],
            reason_not_in_clan_ids: [],
            reason_banned_account_ids: [],
            ...error.additional_data,
        }

        const extra = {
            ...additionalData,
            accountId,
            members,
            currency,
            messages: {
                banned: messages.change_commander_account_banned,
            },
        }

        const messagesSequence = getErrorMessage(RESOURCE_ALLOCATION_ERRORS, reason, extra)

        return Array.isArray(messagesSequence) ?
            messagesSequence.map(message => dispatch(resourceAllocationErrorSingleNotification(reason, message))) :
            dispatch(resourceAllocationErrorSingleNotification(reason, messagesSequence))
    }
}

export const resourceAllocationErrorSingleNotification = (reason, message) => ({
    type: RESOURCE_ALLOCATION_ERROR,
    payload: {},
    meta: {
        webToClient: {
            handler: 'sendNotification',
            params: {
                type: 'ErrorSimple',
                header: getErrorHeader(RESOURCE_ALLOCATION_ERROR_HEADERS, reason),
                message,
            },
        },
    },
})



// WEBPACK FOOTER //
// ./state/actions/CMT/Notifications/ResourceAllocation.js